import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowPathIcon, CreditCardIcon, ExclamationTriangleIcon, CheckCircleIcon, DocumentArrowDownIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { useToast } from '@/hooks/useToast';
import { listFoodBenefitImportsByCompetence } from '../services/foodBenefitImportsService';
import { listPayrollImportsByCompetence } from '../services/payrollImportsService';
import type {
  FoodBenefitDistribution,
  FoodBenefitImportDocument,
  FoodBenefitImportRecordPreview,
  FoodBenefitOrder,
} from '../services/foodBenefitTypes';
import { FOOD_BENEFIT_DISTRIBUTION_KEYS, createEmptyFoodBenefitDistribution } from '../services/foodBenefitTypes';

type AnnotatedOrder = FoodBenefitOrder & {
  importId: string;
};

type FlattenedRecord = FoodBenefitImportRecordPreview & {
  importId: string;
};

const DISTRIBUTION_COLUMNS: Array<{ key: keyof FoodBenefitDistribution; label: string }> = [
  { key: 'meal', label: 'Refeição' },
  { key: 'food', label: 'Alimentação' },
  { key: 'mobility', label: 'Mobilidade' },
  { key: 'culture', label: 'Cultura' },
  { key: 'health', label: 'Saúde' },
  { key: 'education', label: 'Educação' },
  { key: 'homeOffice', label: 'Home office' },
  { key: 'freeBalance', label: 'Saldo livre' },
  { key: 'multi', label: 'Multi' },
];

const formatCurrency = (value: number): string => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatOrderDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const formatCpf = (cpf?: string | null): string => {
  if (!cpf) return '—';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const normalizeCompetence = (value?: string | null, year?: string | number | null, month?: string | number | null): string => {
  const trimmed = (value ?? '').trim();
  if (trimmed) {
    if (/^\d{6}$/.test(trimmed)) {
      return `${trimmed.slice(0, 4)}-${trimmed.slice(4)}`;
    }
    return trimmed;
  }
  if (year == null || month == null) return '';
  const y = String(year).padStart(4, '0');
  const m = String(month).padStart(2, '0');
  return `${y}-${m}`;
};

function DistributionList({ record }: { record: FoodBenefitDistribution }) {
  const entries = DISTRIBUTION_COLUMNS.filter((column) => record[column.key] > 0);
  if (!entries.length) {
    return <span className="text-sm text-gray-500">Sem valores</span>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map((column) => (
        <span
          key={column.key}
          className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-xs text-gray-700"
        >
          <span className="font-medium text-gray-900">{column.label}:</span>
          <span>{formatCurrency(record[column.key])}</span>
        </span>
      ))}
    </div>
  );
}

export function FoodBenefitImportDetailsPage() {
  const { competence = '' } = useParams();
  const { error: showError } = useToast();
  const [imports, setImports] = useState<FoodBenefitImportDocument[]>([]);
  const [deduction202233, setDeduction202233] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const data = await listFoodBenefitImportsByCompetence(competence);
        if (!active) return;
        setImports(data);
        if (!data.length) {
          setLoadError('Nenhuma importação encontrada para esta competência.');
        } else {
          setLoadError(null);
        }
      } catch (error) {
        console.error('[FoodBenefitImportDetailsPage] failed to load imports', error);
        if (!active) return;
        const message = 'Não foi possível carregar a importação.';
        setLoadError(message);
        showError(message);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (!competence) {
      setImports([]);
      setLoading(false);
      setLoadError('Competência não encontrada.');
      return () => {
        active = false;
      };
    }

    void fetchDetails();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competence]);

  useEffect(() => {
    const fetchPayrollDeductions = async () => {
      if (!competence && !imports.length) {
        setDeduction202233(0);
        return;
      }
      try {
        const firstImport = imports[0];
        const targetCompetence =
          normalizeCompetence(competence, null, null) ||
          normalizeCompetence(firstImport?.billingPeriod, null, null);
        if (!targetCompetence) {
          setDeduction202233(0);
          return;
        }

        const payrollImports = await listPayrollImportsByCompetence(targetCompetence);
        if (!payrollImports.length) {
          console.warn('[FoodBenefitImportDetailsPage] Nenhum payroll import encontrado para competência', targetCompetence);
        }

        const total = payrollImports.reduce((acc, imp) => {
          const byCode = imp.totals?.deduction?.byCode ?? {};
          const raw202 = byCode['202']?.total;
          const raw233 = byCode['233']?.total;
          const val202 = Number.isFinite(Number(raw202)) ? Number(raw202) : 0;
          const val233 = Number.isFinite(Number(raw233)) ? Number(raw233) : 0;

          return acc + val202 + val233;
        }, 0);
        setDeduction202233(total);
      } catch (error) {
        console.error('[FoodBenefitImportDetailsPage] failed to load payroll deductions 202/233', error);
        setDeduction202233(0);
      }
    };

    void fetchPayrollDeductions();
  }, [competence, imports]);

  const orders = useMemo<AnnotatedOrder[]>(() => {
    return imports
      .flatMap((importDoc) =>
        (importDoc.orders ?? []).map((order) => ({
          ...order,
          importId: importDoc.id,
        })),
      )
      .sort((a, b) => {
        const dateA = a.orderDate ? new Date(a.orderDate).getTime() : 0;
        const dateB = b.orderDate ? new Date(b.orderDate).getTime() : 0;
        return dateB - dateA;
      });
  }, [imports]);

  const summary = useMemo(() => {
    return orders.reduce(
      (acc, order) => {
        acc.totalAmount += order.totalAmount ?? 0;
        acc.totalOrders += 1;
        return acc;
      },
      { totalAmount: 0, totalOrders: 0 },
    );
  }, [orders]);

  const aggregatedBreakdown = useMemo(() => {
    const totals = createEmptyFoodBenefitDistribution();
    imports.forEach((importDoc) => {
      FOOD_BENEFIT_DISTRIBUTION_KEYS.forEach((key) => {
        totals[key] += importDoc.totals?.breakdown?.[key] ?? 0;
      });
    });
    return totals;
  }, [imports]);

  const flattenedRecords = useMemo<FlattenedRecord[]>(() => {
    return imports
      .flatMap((importDoc) =>
        (importDoc.recordsPreview ?? []).map((record) => ({
          ...record,
          importId: importDoc.id,
        })),
      )
      .sort((a, b) => {
        if ((b.amount ?? 0) !== (a.amount ?? 0)) return (b.amount ?? 0) - (a.amount ?? 0);
        return (a.name ?? 'Sem nome').localeCompare(b.name ?? 'Sem nome', 'pt-BR');
      });
  }, [imports]);

  const auditItems = useMemo(() => {
    return imports.flatMap((doc) => doc.audit || []);
  }, [imports]);

  const handleExportPdf = () => {
    const doc = new jsPDF();
    doc.text(`Auditoria de Benefício Alimentação - ${competence}`, 14, 10);

    const tableData = auditItems.map((item) => [
      item.beneficiaryName,
      formatCurrency(item.value ?? 0),
      item.reason,
    ]);

    autoTable(doc, {
      head: [['Beneficiário', 'Valor', 'Motivo']],
      body: tableData,
      startY: 20,
    });

    doc.save(`auditoria-alimentacao-${competence}.pdf`);
  };

  const handleExportCsv = () => {
    const headers = ['Beneficiário', 'CPF', 'Valor', 'Motivo'];
    const rows = auditItems.map((item) => [
      item.beneficiaryName,
      item.beneficiaryCpf || '',
      (item.value || 0).toFixed(2).replace('.', ','),
      item.reason,
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(';')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `auditoria-alimentacao-${competence}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses/benefits/food"
        icon={<CreditCardIcon className="h-5 w-5" aria-hidden />}
        badge="Auxílio alimentação"
        title={`Competência ${competence}`}
        description="Pedidos importados nesta competência e sua distribuição por categoria."
      />

      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Resumo</h2>
            <p className="text-sm text-gray-600">Totais consolidados das importações da competência selecionada.</p>
          </div>
          {loading ? <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-500" aria-hidden /> : null}
        </div>

        {loadError ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{loadError}</div>
        ) : null}
        {!loading && !loadError ? (
          <p className="mt-2 text-sm text-gray-600">Pedidos importados: {summary.totalOrders}</p>
        ) : null}

        {!loading && !loadError ? (
          <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-md border border-gray-200 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Valor total</dt>
              <dd className="text-lg font-semibold text-gray-900">{formatCurrency(summary.totalAmount)}</dd>
              <dd className="mt-1 text-xs font-semibold text-red-600">
                (-) Dedução 202/233: {formatCurrency(deduction202233 ?? 0)}
              </dd>
            </div>
            <div className="rounded-md border border-gray-200 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Refeição + Alimentação</dt>
              <dd className="text-lg font-semibold text-gray-900">
                {formatCurrency(aggregatedBreakdown.meal + aggregatedBreakdown.food)}
              </dd>
            </div>
            <div className="rounded-md border border-gray-200 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Saldo livre</dt>
              <dd className="text-lg font-semibold text-gray-900">
                {formatCurrency(aggregatedBreakdown.freeBalance)}
              </dd>
            </div>
          </dl>
        ) : null}
      </div>

      <div className="bg-white shadow sm:rounded-lg overflow-hidden border border-gray-200">
        <div
          className={`px-4 py-5 sm:px-6 border-b ${auditItems.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3
                className={`text-lg leading-6 font-medium flex items-center gap-2 ${auditItems.length > 0 ? 'text-red-800' : 'text-green-800'
                  }`}
              >
                {auditItems.length > 0 ? (
                  <ExclamationTriangleIcon className="h-5 w-5" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5" />
                )}
                Auditoria Global ({auditItems.length})
              </h3>
              <p className={`mt-1 max-w-2xl text-sm ${auditItems.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {auditItems.length > 0
                  ? 'Inconsistências encontradas em todos os arquivos importados para esta competência.'
                  : 'Não foram encontradas pendências de auditoria no arquivo.'}
              </p>
            </div>
            {auditItems.length > 0 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <TableCellsIcon className="h-4 w-4 text-gray-500" />
                  CSV
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 text-gray-500" />
                  PDF
                </button>
              </div>
            )}
          </div>
        </div>
        {auditItems.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Beneficiário
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Valor
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Motivo
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditItems.map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.beneficiaryName}
                      <div className="text-xs text-gray-500">{formatCpf(item.beneficiaryCpf)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(item.value ?? 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      {item.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !loadError && orders.length > 0 ? (
        <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Pedidos dessa Competência</h2>
              <p className="text-sm text-gray-600">ID do pedido, empresa, valor e status de cada arquivo importado.</p>
            </div>
          </div>

          <div className="mt-4 overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">ID do pedido</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Empresa</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Valor</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {orders.map((order) => (
                  <tr key={`${order.importId}-${order.orderId}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{order.orderId}</td>
                    <td className="px-4 py-3 text-gray-800">{order.company ?? '—'}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{formatCurrency(order.totalAmount ?? 0)}</td>
                    <td className="px-4 py-3 text-gray-800">{order.status ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-800">{formatOrderDate(order.orderDate ?? null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {!loading && !loadError && flattenedRecords.length > 0 ? (
        <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Colaboradores</h2>
              <p className="text-sm text-gray-600">Detalhamento de pedidos por titular e distribuição de valores.</p>
            </div>
          </div>

          <div className="mt-4 overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Titular</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">CPF</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Valor total</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Saldos</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Colaborador vinculado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {flattenedRecords.map((record) => (
                  <tr key={`${record.importId}-${record.code}-${record.name}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{record.name ?? 'Sem nome'}</td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-800 whitespace-nowrap">{formatCpf(record.code)}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{formatCurrency(record.amount ?? 0)}</td>
                    <td className="px-4 py-3">
                      <DistributionList record={record} />
                    </td>
                    <td className="px-4 py-3 text-gray-800">
                      {record.collaboratorId ? 'Vinculado' : <span className="text-gray-500">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
